import AddIcon from "@mui/icons-material/Add";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Box, Divider, IconButton, Typography } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import { type FC, type KeyboardEvent as ReactKeyboardEvent, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../components/InHouseTooltip/InHouseTooltip";
import { CommonHeader } from "../../../components/UIToolkit/molecules/CommonHeader";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { CHARGE, COVERAGE, GRID_FONT, GRID_RADIUS, INK, NEUTRAL, OVERTIME } from "../../../theme/tokens";
import type { AssignedName, CoverageDayCell } from "./types";

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

type Props = {
	/** Row context, already formatted (e.g. "ICU North · RN · 7a–7p"). */
	rowLabel: string;
	/** Column day label, already formatted (e.g. "Tue, Jun 23"). */
	dayLabel: string;
	cell: CoverageDayCell;
	onClose: () => void;
	/**
	 * When supplied, each active assigned name gets an Edit affordance that opens
	 * the recurring-shift editor for that nurse; the footer Add creates one. Both
	 * close the popover first. Omitted in mock / non-editable renders.
	 */
	onEditAssignment?: (assignment: AssignedName) => void;
	onCreate?: () => void;
	/** Editing is disabled for the account (`disable_edit_master_schedule`). */
	editBlocked?: boolean;
	editBlockedTooltip?: string;
};

/**
 * Fix-in-place cell popover (live data): assigned names with PTO flagged, the open-slot
 * count, an inline Add affordance, and a stubbed copy-to-fill drag target. master-schedule-prd §7.1.
 */
export const CoverageCellPopover: FC<Props> = ({ rowLabel, dayLabel, cell, onClose, onEditAssignment, onCreate, editBlocked, editBlockedTooltip }) => {
	const { t } = useTranslation();
	const openSlots = Math.max(0, cell.target - cell.filled);
	const canEdit = !!onEditAssignment && !editBlocked;
	const canCreate = !!onCreate && !editBlocked;
	const ptoNames = cell.names.filter((n) => n.onPto);
	const activeNames = cell.names.filter((n) => !n.onPto);

	const reduceMotion = useReducedMotion();
	const rootRef = useRef<HTMLDivElement>(null);

	// Focus management: capture the cell that opened us so we can restore focus
	// to it on close, then move focus into the popover (its dialog container).
	useEffect(() => {
		const origin = document.activeElement as HTMLElement | null;
		rootRef.current?.focus();
		return () => origin?.focus?.();
	}, []);

	// Escape closes; Tab/Shift+Tab is trapped to the focusable controls inside.
	const handleKeyDown = useCallback(
		(event: ReactKeyboardEvent<HTMLDivElement>) => {
			if (event.key === "Escape") {
				event.stopPropagation();
				onClose();
				return;
			}
			if (event.key !== "Tab" || !rootRef.current) return;
			const focusable = Array.from(rootRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
				(el) => el.offsetParent !== null || el === rootRef.current,
			);
			if (focusable.length === 0) {
				event.preventDefault();
				return;
			}
			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			const active = document.activeElement;
			if (event.shiftKey && (active === first || active === rootRef.current)) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && active === last) {
				event.preventDefault();
				first.focus();
			}
		},
		[onClose],
	);

	return (
		<Box
			ref={rootRef}
			component={motion.div}
			role="dialog"
			aria-label={`${rowLabel} — ${dayLabel}`}
			tabIndex={-1}
			onKeyDown={handleKeyDown}
			initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.95 }}
			animate={reduceMotion ? { opacity: 1 } : { opacity: 1, scale: 1 }}
			transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
			sx={{ width: 264, p: 1.5, display: "flex", flexDirection: "column", gap: 1, outline: "none" }}
			data-testid="ms3-cell-popover"
		>
			<CommonHeader title={dayLabel} onClose={onClose} showDivider={false} titleSx={{ fontSize: 14, fontWeight: 700 }} />
			<Typography sx={{ fontSize: GRID_FONT.BASE, color: INK.MUTED, mt: -0.5 }}>{rowLabel}</Typography>

			<Box sx={{ display: "flex", gap: 0.5, alignItems: "baseline" }}>
				<Typography sx={{ fontSize: GRID_FONT.MD, fontWeight: 600, color: INK.SECONDARY }}>
					{t("schedule.template.v3.popover.filled", { filled: cell.filled, target: cell.target })}
				</Typography>
				{ptoNames.length > 0 && (
					<Typography sx={{ fontSize: GRID_FONT.BASE, color: COVERAGE.WARNING_INK }}>
						{t("schedule.template.v3.popover.ptoCount", { count: ptoNames.length })}
					</Typography>
				)}
			</Box>

			<Divider />

			<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
				{activeNames.map((n) => (
					<Box
						key={n.id}
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							borderRadius: GRID_RADIUS.CHIP,
							"& .ms3-popover-edit": { opacity: { xs: 1, sm: 0 }, transition: "opacity 120ms" },
							"@media (hover: hover) and (pointer: fine)": {
								"&:hover": { backgroundColor: NEUTRAL[100] },
								"&:hover .ms3-popover-edit": { opacity: 1 },
							},
						}}
					>
						<DragIndicatorIcon sx={{ fontSize: 16, color: NEUTRAL[400], cursor: "grab" }} />
						<Typography sx={{ fontSize: GRID_FONT.MD, color: INK.PRIMARY }}>{n.name}</Typography>
						{n.charge && (
							<Box
								component="span"
								aria-label={t("schedule.template.v3.detail.chargeAria")}
								sx={{
									fontSize: GRID_FONT.MICRO,
									fontWeight: 800,
									letterSpacing: GRID_FONT.LETTER_SPACING.HEADER,
									color: CHARGE.INK,
									backgroundColor: CHARGE.BG,
									px: 0.5,
									borderRadius: GRID_RADIUS.BADGE,
								}}
							>
								{t("schedule.template.v3.detail.charge")}
							</Box>
						)}
						{n.ot && (
							<Box
								component="span"
								sx={{
									fontSize: GRID_FONT.MICRO,
									fontWeight: 800,
									color: OVERTIME.INK,
									backgroundColor: OVERTIME.BG,
									px: 0.5,
									borderRadius: GRID_RADIUS.BADGE,
									opacity: n.ot === "approaching" ? 0.7 : 1,
								}}
							>
								{t("schedule.template.v3.people.otBadge")}
							</Box>
						)}
						<Typography sx={{ fontSize: GRID_FONT.SM, color: INK.MUTED, ml: "auto" }}>{n.role}</Typography>
						{canEdit && (
							<InHouseTooltip title={t("schedule.template.v3.recurring.editAria", { name: n.name })}>
								<IconButton
									className="ms3-popover-edit"
									size="small"
									onClick={() => {
										onClose();
										onEditAssignment?.(n);
									}}
									aria-label={t("schedule.template.v3.recurring.editAria", { name: n.name })}
									data-testid={`ms3-cell-popover-edit-${n.id}`}
									sx={{ p: 0.25 }}
								>
									<EditOutlinedIcon sx={{ fontSize: 15, color: INK.MUTED }} />
								</IconButton>
							</InHouseTooltip>
						)}
					</Box>
				))}
				{ptoNames.map((n) => (
					<Box key={n.id} sx={{ display: "flex", alignItems: "center", gap: 1, opacity: 0.7 }}>
						<BeachAccessIcon sx={{ fontSize: 15, color: COVERAGE.WARNING_INK }} />
						<Typography sx={{ fontSize: GRID_FONT.MD, color: INK.SECONDARY, textDecoration: "line-through" }}>{n.name}</Typography>
						<Typography sx={{ fontSize: GRID_FONT.SM, color: COVERAGE.WARNING_INK, ml: "auto" }}>{t("schedule.template.v3.popover.onPto")}</Typography>
					</Box>
				))}
			</Box>

			{openSlots > 0 && (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						gap: 0.5,
						border: `1px dashed ${NEUTRAL[400]}`,
						borderRadius: 1,
						py: 0.75,
						color: INK.MUTED,
						fontSize: GRID_FONT.BASE,
					}}
					data-testid="ms3-cell-popover-open-slots"
				>
					{t("schedule.template.v3.popover.openSlots", { count: openSlots })}
				</Box>
			)}

			{onCreate && (
				<SystemButton
					buttonVariant="secondary"
					size="small"
					leadingIcon={<AddIcon sx={{ fontSize: 16 }} />}
					noFullWidthMobile
					sx={{ width: "100%" }}
					disabled={!canCreate}
					tooltip={editBlocked ? editBlockedTooltip : undefined}
					onClick={() => {
						onClose();
						onCreate();
					}}
					data-testid="ms3-cell-popover-add"
				>
					{t("schedule.template.v3.popover.add")}
				</SystemButton>
			)}
		</Box>
	);
};
